import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import type { IGoogleAdsAccountOption } from "app/pages/digital-marketing/shared/google-ads/hooks/use-google-ads-accounts";
import { useGoogleAdsConnection } from "app/pages/digital-marketing/shared/google-ads/hooks/use-google-ads-oauth";
import {
    callGoogleAdsProxy,
    GOOGLE_ADS_API_VERSION_PATH,
} from "app/pages/digital-marketing/shared/google-ads/services/google-ads-proxy-client";

interface IGoogleAdsAdGroupKeywordCriteriaResponse {
    results?: { adGroupCriterion?: { keyword?: { text?: string } } }[];
}

const buildAdGroupKeywordsQuery = (adGroupId: string): string =>
    `SELECT ad_group_criterion.keyword.text FROM ad_group_criterion WHERE ad_group.id = ${adGroupId} AND ad_group_criterion.type = KEYWORD AND ad_group_criterion.negative = FALSE AND ad_group_criterion.status = ENABLED`;

export const fetchGoogleAdsAdGroupKeywords = async (
    account: IGoogleAdsAccountOption,
    adGroupId: string,
): Promise<string[]> => {
    const response = await callGoogleAdsProxy<IGoogleAdsAdGroupKeywordCriteriaResponse>({
        method: "POST",
        path: `${GOOGLE_ADS_API_VERSION_PATH}/customers/${account.id}/googleAds:search`,
        body: { query: buildAdGroupKeywordsQuery(adGroupId) },
        loginCustomerId: account.loginCustomerId,
    });

    const keywordTexts = (response.results ?? [])
        .map((result) => result.adGroupCriterion?.keyword?.text)
        .filter((text): text is string => Boolean(text));

    // Two enabled keyword criteria in the same ad group can target the same text with
    // different match types (SIM-107 drops match type on import), so dedupe case-insensitively.
    const dedupedByLowerCase = new Map<string, string>();
    for (const text of keywordTexts) {
        const key = text.toLowerCase().trim();
        if (!dedupedByLowerCase.has(key)) {
            dedupedByLowerCase.set(key, text);
        }
    }

    return [...dedupedByLowerCase.values()];
};

export const useGoogleAdsAdGroupKeywords = (
    account: IGoogleAdsAccountOption | null,
    adGroupId: string | null,
): UseQueryResult<string[]> => {
    const { isConnected, connectionId } = useGoogleAdsConnection();

    return useQuery({
        queryKey: ["googleAdsAdGroupKeywords", connectionId, account?.id, adGroupId],
        queryFn: () => fetchGoogleAdsAdGroupKeywords(account as IGoogleAdsAccountOption, adGroupId as string),
        enabled: isConnected && Boolean(account) && Boolean(adGroupId),
        retry: false,
    });
};
